import {HttpClient} from "@angular/common/http";
import {Injectable} from '@angular/core';
import {Store} from "@ngrx/store";
import {BehaviorSubject, Observable, of, throwError} from "rxjs";
import {map, mergeMap, take, tap} from "rxjs/operators";
import {selectUsers} from "src/app/modules/users/store/user.selectors";
import {environment} from "@env/environment";
import {RegistrationRequest} from "../../model/registrationRequest";
import {AddUserResponse, User, UserListResponse} from "./users.interface";
import {ErrorService} from "@services/error.service";
import {NotificationService} from "@services/notification.service";

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private store: Store,
    private errorService: ErrorService,
    private notificationService: NotificationService,
  ) { }

  loadUsers(): Observable<User[]> {
    return this.store
      .select(selectUsers)
      .pipe(
        take(1),
        mergeMap(users => {
          if (users?.length > 0) {
            return of(users);
          } else {
            this.isLoading$.next(true);

            return this.http.get<UserListResponse>(`${environment.DASHBOARD_PRIVATE_BASE_URL}/listaUtenti`).pipe(
              tap(response => {
                this.isLoading$.next(false);

                if (response.status.codice !== '000') {
                  throwError("Si è verificato un errore nel caricare la lista utenti")
                }
              }),
              map(response => response.data.utenti)
            )
          }
      })
    );
  }

  addUser(data): Observable<User> {
    this.isLoading$.next(true);
    const newUser: RegistrationRequest = {
      nome: 'utente',
      cognome: 'dashboard',
      cf: 'TNTDHB80A41Z404Z',
      username: data.email,
      password: 'Iren123456789!',
      gruppoAutorizzazione: '9000 - Iren Mercato',
      tipoAccount: 'RES',
      normativa: 'true',
      condizioni: 'true',
      socialType: null,
      telefono: '3331234567'
    }

    const req = {
      request: data,
      newUser
    }

    return this.http.post<AddUserResponse>(`${environment.DASHBOARD_PRIVATE_BASE_URL}/aggiungiUtente`, req).pipe(
      tap(response => {
        this.isLoading$.next(false);
        if (response.status.codice !== '000') {
          const errorMessage = "Si è verificato il seguente errore nel corso della registrazione dell'utente sul portale Iren: " + response.status.descrizione
          this.errorService.setError(errorMessage)
          throw new Error(errorMessage)
        } else {
          this.notificationService.setNotification("Utente aggiunto con successo")
        }
      }),
      map(response => response.data),
      map(response => {
        return {
          id_utente: response.id_utente,
          email: response.email,
          password: null,
          old_password: null,
          new_password: null,
          ruolo: response.ruolo,
          is_developer: response.is_developer,
          token: response.token,
          nomeRuolo: null
        }
      })
    )
  }

  deleteUser(user: User): Observable<User> {
    this.isLoading$.next(true);

    return this.http.get<AddUserResponse>(`${environment.DASHBOARD_PRIVATE_BASE_URL}/eliminaUtente/${user.id_utente}`).pipe(
      tap(response => {
        this.isLoading$.next(false);
        if (response.status.codice !== '000') {
          const errorMessage = "Si è verificato il seguente errore nel corso della cancellazione dell'utente sul portale Iren: " + response.status.descrizione
          this.errorService.setError(errorMessage);
        } else {
          this.notificationService.setNotification("Utente cancellato con successo!")
        }
      }),
      map(response => response.data),
      map(response => {
        return {
          id_utente: response.id_utente,
          email: response.email,
          password: null,
          old_password: null,
          new_password: null,
          ruolo: response.ruolo,
          is_developer: response.is_developer,
          token: response.token,
          nomeRuolo: null
        }
      })
    )
  }

  updateUser(request): Observable<User> {
    this.isLoading$.next(true);
    return this.http.post(`${environment.DASHBOARD_PRIVATE_BASE_URL}/modificaUtente`, request).pipe(
      tap(risposta => {
        this.isLoading$.next(false);
        if (risposta['codice'] !== '000') {
          const errorMessage = "Si è verificato il seguente errore nel corso della modifica dell'utente sul portale Iren: " + risposta['status'].descrizione
          this.errorService.setError(errorMessage)
          throw new Error(errorMessage)
        } else {
          this.notificationService.setNotification("Utente modificato con successo")
        }
      }),
      map(response => request.user)
    )
  }
}
